import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Clock, Image as ImageIcon, Truck, LayoutGrid, Columns3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Seo } from '../components/Seo';
import { PackageCompare } from '../components/PackageCompare';
import { usePageContent } from '../hooks/usePageContent';
import { CATEGORIES, packagesFor, priceLabel, hours, SHARED_RULES, categorySlug } from '../lib/packageData';

const HERO_BG = 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=1920';

const PackageCard = ({ pkg }) => (
  <Card className="bg-[#2A2420] border-[#C5A880]/20 h-full" data-testid={`pkg-${pkg.id}`}>
    <CardContent className="p-6 flex flex-col h-full">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-[#EADBC8] font-semibold text-lg">{pkg.name}</h3>
        <span className="text-[#C5A880] font-bold whitespace-nowrap">{priceLabel(pkg)}</span>
      </div>
      <div className="space-y-2 text-sm text-[#EADBC8]/70 flex-1">
        {pkg.minutes && <p className="flex items-center gap-2"><Clock size={15} className="text-[#C5A880]" /> {hours(pkg.minutes)} coverage</p>}
        {pkg.finished_images != null && <p className="flex items-center gap-2"><ImageIcon size={15} className="text-[#C5A880]" /> {typeof pkg.finished_images === 'number' ? `${pkg.finished_images} edited images` : pkg.finished_images}</p>}
        {pkg.delivery && <p className="flex items-center gap-2"><Truck size={15} className="text-[#C5A880]" /> {pkg.delivery}</p>}
        {pkg.scope && <p className="text-[#EADBC8]/55 text-xs pt-1">{pkg.scope}</p>}
      </div>
      <Link to="/quote" className="mt-5"><Button className="w-full bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold">Request a quote</Button></Link>
    </CardContent>
  </Card>
);

const Photography = () => {
  const { t } = usePageContent('photography');
  const [catId, setCatId] = useState(1);
  const [view, setView] = useState('cards');
  const list = packagesFor('Photography', catId);

  return (
    <div className="min-h-screen bg-[#1F1A17]">
      <Seo
        exactTitle="Photography Sessions & Event Coverage | Mayden Films"
        description="Mayden Films photography: weddings from $1,499, celebrations from $399, portrait sessions from $249, plus business, commercial, automotive, sport and performance coverage. AUD, no GST."
        path="/photography"
      />

      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={t('hero_image', HERO_BG)} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1F1A17]/85 via-[#1F1A17]/90 to-[#1F1A17]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C5A880] tracking-[0.25em] uppercase text-xs font-semibold mb-4">{t('hero_eyebrow', 'Photography')}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EADBC8] mb-5">{t('hero_title', 'Photography Sessions & Event Coverage')}</h1>
          <p className="text-base md:text-lg text-[#EADBC8]/70 max-w-2xl mx-auto mb-6">
            {t('hero_subtitle', 'Planned coverage, clear packages and professionally edited photographs delivered online. Weddings from $1,499, celebrations from $399, portrait sessions from $249.')}
          </p>
          <Link to="/quote"><Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-8 py-6 text-base">Request a photography quote</Button></Link>
        </div>
      </section>

      <section className="py-16 bg-[#1A1612]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mb-2 text-center">Packages by category</h2>
          <p className="text-[#EADBC8]/55 text-sm text-center mb-8">Choose a category to see the approved packages. A custom quote is available for any brief.</p>
          <div className="flex flex-nowrap sm:flex-wrap gap-2 mb-6 overflow-x-auto sm:justify-center pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setCatId(c.id)} data-testid={`photo-cat-${c.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 transition-colors ${catId === c.id ? 'bg-[#C5A880] text-[#1F1A17]' : 'bg-[#2A2420] text-[#EADBC8]/70 hover:text-[#C5A880] border border-[#C5A880]/20'}`}>
                {c.name}
              </button>
            ))}
          </div>
          {list.length > 1 && (
            <div className="flex justify-center mb-8">
              <div className="inline-flex rounded-full border border-[#C5A880]/25 bg-[#2A2420] p-1" role="group" aria-label="View mode">
                <button onClick={() => setView('cards')} data-testid="photo-view-cards"
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === 'cards' ? 'bg-[#C5A880] text-[#1F1A17]' : 'text-[#EADBC8]/65 hover:text-[#C5A880]'}`}>
                  <LayoutGrid size={15} /> Cards
                </button>
                <button onClick={() => setView('compare')} data-testid="photo-view-compare"
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === 'compare' ? 'bg-[#C5A880] text-[#1F1A17]' : 'text-[#EADBC8]/65 hover:text-[#C5A880]'}`}>
                  <Columns3 size={15} /> Compare
                </button>
              </div>
            </div>
          )}
          {view === 'compare' && list.length > 1 ? (
            <PackageCompare service="Photography" packages={list} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="photo-package-grid">
              {list.map((p) => <PackageCard key={p.id} pkg={p} />)}
            </div>
          )}
          {list.length === 0 && <p className="text-[#EADBC8]/50 text-center">This category is available as a custom quote — tell us about your project.</p>}
          <div className="text-center mt-8">
            <Link to={`/portfolio?category=${categorySlug(catId)}`} className="inline-flex items-center gap-1.5 text-[#C5A880] hover:text-[#D4BC95] font-medium text-sm transition-colors" data-testid="photo-related-work">
              See related work in {CATEGORIES.find((c) => c.id === catId)?.name} →
            </Link>
          </div>
          <p className="text-[#EADBC8]/45 text-xs text-center mt-4">A category alone does not guarantee that every project fits its entry price. Complex commercial and creative work is quoted individually.</p>
        </div>
      </section>

      <section className="py-16 bg-[#1F1A17]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mb-6 text-center">Pricing &amp; booking</h2>
          <ul className="space-y-3">
            {SHARED_RULES.map((r) => (
              <li key={r} className="flex items-start text-[#EADBC8]/75 text-sm"><Check size={18} className="text-[#C5A880] mr-3 mt-0.5 flex-shrink-0" />{r}</li>
            ))}
          </ul>
          <div className="text-center mt-10">
            <Link to="/portfolio"><Button variant="outline" className="border-2 border-[#C5A880] text-[#C5A880] hover:bg-[#C5A880] hover:text-[#1F1A17] font-semibold px-8 py-5">View Our Work</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Photography;
